import React, { useState, useRef } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Phone, Mail, MapPin, Send, ArrowRight, CheckCircle, Facebook, Linkedin, Instagram, ExternalLink } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const ContactUs = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);

  // Exact office coordinates provided by the client
  const position = [17.4339169274257, 78.40427231187154]; // Precise location for Jubilee 800, Road No. 36

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaVerified) {
      alert("Please verify that you are not a robot");
      return;
    }

    const SENDY_SUBSCRIBE_URL = "https://maxiomwealth.com/app/api/newsendy/";
    const contact_us_information = ['stock-advisory'];
    const customer_message = formData.name ? `${formData.name} wants to talk with advisor.` : "Customer wants to talk with advisor.";


      // Step 1: Initialize session
      console.log("[DEBUG] Initializing session...");
      const sessionResponse = await fetch(SENDY_SUBSCRIBE_URL, {
        method: 'GET',
        credentials: 'include'
      });
      
      const cookies = sessionResponse.headers.get('set-cookie') || '';
      console.log("[DEBUG] Session cookies:", cookies);

      // Step 2: Prepare payload with all required parameters
      const params = new URLSearchParams();
      params.append('email', formData.email);
      params.append('mobile_number', formData.phone); // Required by API
      params.append('start_my_switch', 'false'); // Required by API
      params.append('name', formData.name);
      contact_us_information.forEach(item => params.append('contact_us_information[]', item));
      params.append('message', formData.message || customer_message);
      params.append('from', '');
      params.append('channel', 'web');
      params.append('campaign', 'web');
      params.append('source', 'https://test.maxiomwealth.com/contact-us');

      console.log("[DEBUG] Complete request payload:", params.toString());

      // Step 3: Submit data
      const response = await fetch(SENDY_SUBSCRIBE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Cookie': cookies,
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: params
      });

      console.log("[DEBUG] Response status:", response.status);
      const responseData = await response.json();
      console.log("[DEBUG] API response:", responseData);

      // Handle response based on API documentation
      setFormSubmitted(true); // Always show Thank You message after submit
      // Reset form fields on success, but keep Thank You message until user clicks button
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }


  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">
        {/* Hero Section with Background Shapes */}
        <div className="bg-gradient-to-r from-[#113262] to-[#1C52A0] text-white py-20 relative overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#F49611] opacity-10 blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#1C52A0] opacity-20 blur-3xl"></div>
          </div>

          {/* Content */}
          <div className="container text-center relative z-10 px-4 pt-20 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">Get in Touch</h1>
            <div className="w-24 h-1 bg-[#F49611] mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-lg text-white/90">
              We're here to answer your questions about wealth management and financial planning.
              Let us help you build a more secure financial future.
            </p>
          </div>
        </div>

        {/* Contact Map & Info Section */}
        <div className="container mt-5 px-4 z-20 relative w-full max-w-6xl mx-auto p-6">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Contact Info Card */}
              <div className="bg-[#113262] text-white p-8 lg:p-10">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <div className="space-y-8">
                  {/* Address */}
                  <div className="flex items-start">
                    <div className="bg-[#F49611]/20 p-3 rounded-full mr-4">
                      <MapPin className="w-6 h-6 text-[#F49611]" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Our Office</h3>
                      <p className="text-white/80 text-sm">
                        C2 (Awfis) 3rd Floor, Plot #800, Road #36,<br />
                        Jubilee Hills, Hyderabad, India - 500033
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start">
                    <div className="bg-[#F49611]/20 p-3 rounded-full mr-4">
                      <Mail className="w-6 h-6 text-[#F49611]" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Email</h3>
                      <a href="mailto:advisor@maxiomwealth.com" className="text-white/80 text-sm hover:text-[#F49611] transition-colors">
                        advisor@maxiomwealth.com
                      </a>
                      <p className="text-white/60 text-xs mt-1">Free enquiry</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="border-t border-primary-300/20 pt-8 mt-12">
                  <h1 className="text-sm font-medium mb-4">Follow Us</h1>
                  <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="flex gap-4">
                      <a href="https://www.facebook.com/maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <Facebook className="w-6 h-6" />
                      </a>
                      <a href="https://x.com/maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                            fill="currentColor" />
                        </svg>
                      </a>
                      <a href="https://www.linkedin.com/company/maxiomwealth/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                          <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                        </svg>
                      </a>
                      <a href="https://www.instagram.com/maxiomwealth/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <Instagram className="w-6 h-6" />
                      </a>
                      <a href="https://maxiomwealth.quora.com/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                          <path
                            d="M14.4484 20.125C15.0078 20.7812 16.188 20.9747 16.788 21C19.7125 20.924 20.2151 18.3701 19.9305 17.0456C18.9678 19.5171 17.0848 18.2255 16.5882 17.4829C16.0915 16.7403 15.5043 15.6705 15.0078 14.9924C12.5374 11.7072 9.71584 13.0152 8.61385 14.0798C11.5052 13.4935 12.9076 16.7577 13.2474 18.4631C12.6494 18.6298 12.0219 18.7186 11.3749 18.7186C7.30185 18.7186 4 15.1999 4 10.8593C4 6.51873 7.30185 3 11.3749 3C15.4479 3 18.7498 6.51873 18.7498 10.8593C18.7498 12.6423 18.4926 13.8375 17.5537 15.1562C17.1683 14.5337 16.0739 13.05 15.4636 12.5938C15.8632 9.72936 15.0078 4.55165 11.3749 4.4981C7.24786 4.43726 6.89425 10.1255 7.23333 12.9772"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </a>
                      <a href="https://www.youtube.com/@maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                            fill="currentColor" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="col-span-2 p-8 lg:p-10">
                {formSubmitted ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
                    <p className="text-gray-600 mb-6">Your message has been sent successfully. We'll get back to you shortly.</p>
                    <button
                      onClick={() => setFormSubmitted(false)}
                      className="text-[#113262] font-medium flex items-center"
                    >
                      Send another message
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Send Us a Message</h2>
                    <p className="text-gray-600 mb-8">Feel free to reach out with any questions about our services</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name*</label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address*</label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                            placeholder="Your email"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number*</label>
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                          placeholder="Your contact number"
                        />
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Message</label>
                        <textarea
                          id="message"
                          name="message"
                          rows="5"
                          value={formData.message}
                          onChange={handleChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1C52A0] focus:border-transparent transition-all bg-gray-50"
                          placeholder="Type your message here..."
                        ></textarea>
                      </div>

                      {/* reCAPTCHA */}
                      <div>
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          // sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                          sitekey="6Lcf8EkpAAAAAEv6vHns8_A00GNzr76o-nXaE7lo" // Replace with your actual site key when in production
                          onChange={handleCaptchaChange}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          This site is protected by reCAPTCHA and the Google{' '}
                          <a href="https://policies.google.com/privacy" className="text-[#1C52A0] hover:underline">Privacy Policy</a> and{' '}
                          <a href="https://policies.google.com/terms" className="text-[#1C52A0] hover:underline">Terms of Service</a> apply.
                        </p>
                      </div>

                      <button
                        type="submit"
                        className={`bg-gradient-to-r from-[#113262] to-[#1C52A0] text-white py-3 px-8 rounded-lg transition-all flex items-center font-medium ${!captchaVerified ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-95'}`}
                        disabled={!captchaVerified}
                      >
                        Submit Now
                        <Send className="w-4 h-4 ml-2" />
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="container w-full max-w-6xl mx-auto p-6">
          <div className="rounded-xl overflow-hidden shadow-lg h-[400px]">
            <MapContainer
              center={position}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={position}>
                <Popup>
                  <div className="text-center p-1">
                    <strong>Maxiom Wealth</strong><br />
                    C2 (Awfis) 3rd Floor, Plot #800, Road #36,<br />
                    Jubilee Hills, Hyderabad, India - 500033
                    <div className="mt-2">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${position[0]},${position[1]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1C52A0] hover:text-[#F49611] transition-colors flex items-center justify-center text-sm font-medium"
                      >
                        Open in Google Maps
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactUs;